<script setup lang="ts">
import type {
  IMonitorDepositRatesResponse,
  IMonitorDepositRateItem
} from '~/components/widget/monitor'
import { EntityOrganizationLogo } from '~/components/entity/organization-logo'
import { useMonitorColumns, MONITOR_COLUMN_ANIMATION_DELAY_MS } from '../useMonitorColumns'

const DEPOSIT_PERIODS = [3, 6, 9, 12] as const
const DEPOSIT_CURRENCY = 'KGS'

const { t } = useI18n()

const selectedPeriod = ref<number>(12)

const requestParams = computed(() => ({
  period: selectedPeriod.value,
  currency: DEPOSIT_CURRENCY
}))

const { data: response } = useApi<IMonitorDepositRatesResponse>('/monitor/deposit-rates', {
  params: requestParams,
  key: computed(() => `monitor-deposit-rates-${selectedPeriod.value}`),
  watch: [requestParams]
})

// Best rate first, so the most attractive offer leads the row.
const items = computed<IMonitorDepositRateItem[]>(() =>
  [...(response.value?.data ?? [])].sort((a, b) => b.rate - a.rate)
)

const hasItems = computed(() => items.value.length > 0)

// Deposit rates sit in a narrow band, so a floor keeps the lowest bar readable.
const { barHeight, formatRate, logoSize } = useMonitorColumns({
  items,
  rate: (item) => item.rate,
  minBarPercent: 30
})
</script>

<template>
  <div v-if="hasItems" class="Card">
    <div class="mb-4 flex flex-col gap-1">
      <h2 class="text-lg font-semibold">{{ t('monitor.deposit_rates.title') }}</h2>
      <p class="text-muted-foreground text-sm">
        {{ t('monitor.deposit_rates.subtitle', { count: items.length }) }}
      </p>
    </div>

    <div class="mb-6 flex flex-wrap gap-2">
      <Button
        v-for="period in DEPOSIT_PERIODS"
        :key="period"
        :variant="selectedPeriod === period ? 'blue' : 'outline-gray'"
        size="sm"
        @click="selectedPeriod = period"
      >
        {{ t('monitor.deposit_rates.period_months', { count: period }) }}
      </Button>
    </div>

    <Transition name="columns" mode="out-in">
      <ul
        :key="selectedPeriod"
        class="flex items-end gap-2 overflow-x-auto pb-2 sm:gap-3 sm:overflow-x-visible"
      >
        <li
          v-for="(item, index) in items"
          :key="item.organization.id"
          class="MonitorColumn flex min-w-9 max-w-12 shrink-0 grow basis-10 flex-col items-center gap-2 sm:min-w-0 sm:shrink"
          :style="{ '--column-delay': `${index * MONITOR_COLUMN_ANIMATION_DELAY_MS}ms` }"
        >
          <div class="flex h-40 w-full flex-col justify-end gap-1">
            <span class="whitespace-nowrap text-center text-[11px] font-bold tabular-nums">
              {{ formatRate(item.rate) }}
            </span>
            <div
              class="MonitorBar w-full rounded-t-lg"
              :class="index === 0 ? 'bg-blue-600' : 'bg-blue-100'"
              :style="{ '--bar-height': barHeight(item.rate) }"
              role="img"
              :aria-label="
                t('monitor.deposit_rates.bar_label', {
                  org: item.organization.title,
                  rate: formatRate(item.rate)
                })
              "
            />
          </div>

          <EntityOrganizationLogo
            :src="item.organization.logo"
            :alt="item.organization.title"
            :to="item.organization.url"
            :aria-label="
              t('monitor.deposit_rates.more_about_org', { org: item.organization.title })
            "
            :size="logoSize"
          />
        </li>
      </ul>
    </Transition>

    <div class="text-muted-foreground mt-5 border-t pt-3 text-xs">
      {{ t('monitor.deposit_rates.currency_note') }}
    </div>
  </div>
</template>

<style scoped>
/* Bar height comes from the data, so it arrives as a custom property per item. */
.MonitorBar {
  height: var(--bar-height);
}

.columns-enter-active,
.columns-leave-active {
  transition: opacity 0.25s ease;
}

.columns-enter-from,
.columns-leave-to {
  opacity: 0;
}

/* Columns rise one after another, so switching a period reads as a redraw. */
.MonitorColumn {
  animation: monitor-column-rise 0.4s ease-out backwards;
  animation-delay: var(--column-delay);
}

@keyframes monitor-column-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .MonitorColumn {
    animation: none;
  }

  .columns-enter-active,
  .columns-leave-active {
    transition: none;
  }
}
</style>
